import { JsonTextModel } from '../../models/primitive/text.model';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../models/complex/time-recording-entry.model';
import { JsonTimesheetAddressModel } from '../../models/complex/timesheet-address.model';
import { LZStringService } from '../../services/l-z-string/l-z-string.service';
import { AddressState } from '../../state';

export function formatTextModel(t?: JsonTextModel | null): string {
    if (!t) {
        return '--';
    }
    if (t.name && t.value && t.name !== t.value) {
        return `${t.name}: ${t.value}`;
    }
    return t.name || t.value || '';
}

export function formatAddressModel(a?: JsonTimesheetAddressModel | null): string {
    if (!a) {
        return '--';
    }
    const parts = [[a.street, a.addrNumber].filter(Boolean).join(' ').trim(), a.postCode, a.city, a.country].filter(
        Boolean,
    );
    return parts.join(', ') || (a.addrText ?? '—');
}

export function formatAddressState(a?: AddressState | null): string {
    if (!a) {
        return '--';
    }
    const parts = [[a.street, a.addrNumber].filter(Boolean).join(' ').trim(), a.postCode, a.city, a.country].filter(
        Boolean,
    );
    return parts.join(', ') || (a.addrText ?? '—');
}

export function workCodeLabel(code?: string): string {
    if (!code) {
        return '—';
    }
    const map: Record<string, string> = {
        [TIMERECORDING_TYPE_OF_WORK_CODES.BREAK]: 'Break',
        [TIMERECORDING_TYPE_OF_WORK_CODES.OSR]: 'On-Site Repair',
        [TIMERECORDING_TYPE_OF_WORK_CODES.OSR_BILLABLE]: 'On-Site Repair (Billable)',
        [TIMERECORDING_TYPE_OF_WORK_CODES.WAIT]: 'Waiting',
        [TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX]: 'Phone Fix',
        [TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX_EXPENSES]: 'Phone Fix (Expenses)',
        [TIMERECORDING_TYPE_OF_WORK_CODES.TRTO]: 'Travel To',
        [TIMERECORDING_TYPE_OF_WORK_CODES.TRBK]: 'Travel Back',
        [TIMERECORDING_TYPE_OF_WORK_CODES.TRTU]: 'Travel To (Unbillable)',
        [TIMERECORDING_TYPE_OF_WORK_CODES.TRBU]: 'Travel Back (Unbillable)',
        [TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION]: 'Application',
        [TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION_BILLABLE]: 'Application (Billable)',
        [TIMERECORDING_TYPE_OF_WORK_CODES.ECBD]: 'ECBD',
        [TIMERECORDING_TYPE_OF_WORK_CODES.APPLICATION_ECBD]: 'ECBD (Application)',
        [TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY]: 'Travel to Standby',
        [TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY]: 'Travel back from Standby',
        [TIMERECORDING_TYPE_OF_WORK_CODES.SALES_SUPPORT]: 'Sales Support',
        [TIMERECORDING_TYPE_OF_WORK_CODES.OSR_AFTER_HOURS_CALL_OUT]: 'OSR After-Hours Call Out',
        [TIMERECORDING_TYPE_OF_WORK_CODES.ROSR]: 'Remote OSR',
        [TIMERECORDING_TYPE_OF_WORK_CODES.ROSN]: 'Remote OSR (Non-billable)',
        [TIMERECORDING_TYPE_OF_WORK_CODES.FCC]: 'FCC',
        [TIMERECORDING_TYPE_OF_WORK_CODES.FCCN]: 'FCC (Non-billable)',
    };
    return map[code] ?? code;
}

export async function decompress<T>(data?: { file: Blob; timestamp: number }): Promise<{ data?: T; timestamp: Date }> {
    if (!data) {
        return {
            data: undefined,
            timestamp: new Date(0),
        };
    }
    if (!data?.file) {
        return {
            data: undefined,
            timestamp: new Date(data?.timestamp),
        };
    }

    const uint8array = new Uint8Array(await data.file.arrayBuffer());
    const dataString = LZStringService.decompressFromUint8Array(uint8array);
    return {
        data: JSON.parse(dataString) as T,
        timestamp: new Date(data?.timestamp),
    };
}

export function extractDigitsAsNumber(input: string): number {
    const digits = input?.replace(/\D/g, '');
    return digits ? Number(digits) : 0;
}
